import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
  GridFilterPanel,
} from "@mui/x-data-grid-pro";
import { Fragment, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Delete,
  Save,
} from "@mui/icons-material";

type GridFilterPanelProps = React.ComponentProps<typeof GridFilterPanel>;

export interface FilterPresetItem {
  id: string;
  name: string;
  filterCount: number;
}

interface SavedFilterPresetsPanelProps {
  gridFilterPanelProps: GridFilterPanelProps;
  presets: FilterPresetItem[];
  activePresetId: string | null;
  // Extra alert shown in the create dialog (e.g. the local-storage warning)
  createDialogAlert?: string;
  creating?: boolean;
  onCreatePreset: (name: string) => void | Promise<void>;
  onUpdatePreset: (id: string) => void;
  onLoadPreset: (id: string) => void;
  onDeletePreset: (id: string) => void;
}

// The presets column rendered inside the MUI DataGrid filter panel. Purely
// presentational — persistence is supplied by the wrapping panel component
// (ApiSavedFilterPanel: org-scoped for tables, user-scoped for reports).
export function SavedFilterPresetsPanel({
  gridFilterPanelProps,
  presets,
  activePresetId,
  createDialogAlert,
  creating,
  onCreatePreset,
  onUpdatePreset,
  onLoadPreset,
  onDeletePreset,
}: SavedFilterPresetsPanelProps) {
  const apiRef = useGridApiContext();
  const [createFilterDialogOpen, setCreateFilterDialogOpen] = useState(false);
  const [createFilterName, setCreateFilterName] = useState(``);

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const hasActiveFilters = filterModel.items.length > 0;

  const handleCreateFilter = async() => {
    if (!createFilterName.trim()) {
      return;
    }

    await onCreatePreset(createFilterName.trim());

    setCreateFilterDialogOpen(false);
    setCreateFilterName(``);
  };

  return (
    <Fragment>
      <Box
        sx={ {
          minWidth: 256,
          display: `flex`,
          flexDirection: `column`,
          'div:has(&)': { flexWrap: `wrap` },
        } }
      >
        <Box
          sx={ {
            p: 2,
            pb: 1,
            display: `flex`,
            justifyContent: `space-between`,
            alignItems: `center`,
          } }
        >
          <Typography variant={ `subtitle2` }
            sx={ { fontWeight: `bold` } }>
            { `Saved Filter Presets` }
          </Typography>
          <Stack direction={ `row` }
            spacing={ 0.5 }>
            <Tooltip title={ `Create new filter` }>
              <IconButton
                onClick={ () => setCreateFilterDialogOpen(true) }
                size={ `small` }
                color={ `primary` }
              >
                <Add fontSize={ `small` } />
              </IconButton>
            </Tooltip>
            <Tooltip title={ `Update selected filter` }>
              <span>
                <IconButton
                  onClick={ () => activePresetId && onUpdatePreset(activePresetId) }
                  disabled={ !activePresetId }
                  size={ `small` }
                  color={ `primary` }
                >
                  <Save fontSize={ `small` } />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        { presets.length === 0 ? (
          <Box sx={ { p: 2, pt: 0, flexGrow: 1, display: `flex` } }>
            <Box
              sx={ theme => ({
                flex: 1,
                border: `1px solid`,
                borderColor: `divider`,
                borderRadius: 1,
                bgcolor: `grey.100`,
                display: `flex`,
                flexDirection: `column`,
                alignItems: `center`,
                justifyContent: `center`,
                p: 2,
                color: `text.secondary`,
                ...theme.applyStyles(`dark`, {
                  bgcolor: `grey.900`,
                }),
              }) }
            >
              <Typography variant={ `body2` }
                align={ `center` }>
                { `No saved filters yet` }
              </Typography>
            </Box>
          </Box>
        ) : (
          <List dense
            sx={ { pt: 0 } }>
            { presets.map(preset => (
              <ListItem
                key={ preset.id }
                disablePadding
                secondaryAction={
                  <IconButton
                    edge={ `end` }
                    size={ `small` }
                    onClick={ event => {
                      event.stopPropagation();
                      onDeletePreset(preset.id);
                    } }
                  >
                    <Delete fontSize={ `small` } />
                  </IconButton>
                }
              >
                <ListItemButton
                  selected={ preset.id === activePresetId }
                  onClick={ () => onLoadPreset(preset.id) }
                  sx={ {
                    '&.Mui-selected': {
                      bgcolor: `action.selected`,
                      position: `relative`,
                      '&::after': {
                        content: `""`,
                        position: `absolute`,
                        right: 0,
                        top: `50%`,
                        transform: `translateY(-50%)`,
                        width: 3,
                        height: `60%`,
                        bgcolor: `primary.main`,
                        borderRadius: `3px 0 0 3px`,
                      },
                    },
                  } }
                >
                  <ListItemText
                    primary={ preset.name }
                    secondary={
                      <Typography variant={ `caption` }
                        color={ `text.secondary` }>
                        { preset.filterCount } { preset.filterCount === 1 ? `filter` : `filters` }
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )) }
          </List>
        ) }
      </Box>

      <Divider orientation={ `vertical` }
        sx={ { height: `auto` } } />

      <GridFilterPanel { ...gridFilterPanelProps } />

      <Dialog
        open={ createFilterDialogOpen }
        onClose={ () => setCreateFilterDialogOpen(false) }
        maxWidth={ `sm` }
      >
        <DialogTitle>{ `Create new filter` }</DialogTitle>
        <DialogContent>
          <Stack spacing={ 2 }
            sx={ { mt: 1 } }>
            <TextField
              autoFocus
              label={ `Filter Name` }
              value={ createFilterName }
              onChange={ event => setCreateFilterName(event.target.value) }
              fullWidth
              required
            />
            <Alert severity={ `info` }>
              { hasActiveFilters
                ? `This will save the current ${filterModel.items.length} active filter${filterModel.items.length !== 1 ? `s` : ``} as a new preset.`
                : `This will create an empty filter preset that you can configure later.` }
            </Alert>

            { createDialogAlert ? (
              <Alert severity={ `info` }>
                { createDialogAlert }
              </Alert>
            ) : null }
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant={ `outlined` }
            onClick={ () => setCreateFilterDialogOpen(false) }
          >
            { `Cancel` }
          </Button>
          <Button
            onClick={ handleCreateFilter }
            variant={ `contained` }
            disabled={ !createFilterName.trim() || creating }
            startIcon={ <Add /> }
          >
            { `Create Filter` }
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
